import { useRef } from 'react';
import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import { LineChart } from '@/components/analytics/charts/LineChart';
import { useChartDimensions } from '@/hooks/useChartDimensions';
import type { ImprovementTrendData } from '@/types/analytics';
import styles from './SessionsCompleted.module.scss';

export interface SessionsCompletedProps {
  data: ImprovementTrendData[] | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Sessions Completed Widget
 *
 * Displays session completion trend over time with a line chart.
 * Shows total sessions and completion rate.
 */
export function SessionsCompleted({ data, isLoading, error }: SessionsCompletedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useChartDimensions(containerRef as any);

  const isEmpty = !data || data.length === 0;

  // Calculate totals
  const totalSessions = data?.reduce((sum, d) => sum + (d.totalSessions || 0), 0) || 0;

  // Prepare chart data
  const chartSeries = data
    ? [
      {
        name: 'Sessions',
        data: data.map((d) => ({
          x: (d.period || '') as string,
          y: d.totalSessions || 0,
        })),
        color: 'var(--primary)',
      },
    ]
    : [];

  return (
    <WidgetContainer
      title="Sessions Completed"
      description="Practice sessions over time"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Complete some practice sessions to see the trend"
      size="medium"
    >
      <div ref={containerRef} className={styles.sessionsCompleted}>
        {/* Chart */}
        {width > 0 && (
          <div className={styles.sessionsCompleted__chart}>
            <LineChart
              series={chartSeries}
              width={width}
              height={200}
              showDots
              showGrid
              animate
            />
          </div>
        )}

        {/* Summary */}
        <div className={styles.sessionsCompleted__summary}>
          <div className={styles.sessionsCompleted__stat}>
            <span className={styles.sessionsCompleted__statValue}>
              {totalSessions.toLocaleString()}
            </span>
            <span className={styles.sessionsCompleted__statLabel}>Total Sessions</span>
          </div>
        </div>
      </div>
    </WidgetContainer>
  );
}
